<template>
  <v-app-bar
    class="header"
    fixed
    flat
    :app="app"
    :clipped-left="clippedLeft"
    :height="appBarHeight"
  >
    <!-- :hide-on-scroll="$vuetify.breakpoint.smAndDown" -->
    <!-- scroll-threshold="40" -->
    <div class="header-box">
      <div class="header-slot left">
        <div class="slot-item back">
          <v-btn @click="handleBackClick()" icon title="voltar">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </div>

        <div class="slot-item menu" title="Menu">
          <v-fade-transition>
            <slot
              name="open-side-pannel"
              v-if="!sidePanel || !$vuetify.breakpoint.lgAndUp"
            ></slot>
          </v-fade-transition>
        </div>

        <div class="slot-item brand">
          <v-fade-transition duration="0.15">
            <div
              class="brand-item"
              v-show="!sidePanel || !$vuetify.breakpoint.lgAndUp"
            ></div>
          </v-fade-transition>
        </div>

        <div class="slot-item title d-none d-lg-flex">
          <slot name="title"></slot>
        </div>

        <div
          class="slot-item d-flex flex-column flex-nowrap d-lg-none align-start"
        >
          <div class="title text-truncate">
            <slot name="title"></slot>
          </div>
          <div class=" updated-date">
            <slot name="updatedDate"></slot>
          </div>
        </div>

        <div class="slot-item actions" :class="{ scale: sidePanel }">
          <slot name="actions-left"></slot>
        </div>

        <div class="slot-item avatar">
          <slot name="avatar"></slot>
        </div>
      </div>

      <div class="header-slot right">
        <div class="slot-item updated-date d-none d-md-flex">
          <div class="d-none d-lg-flex">
            <slot name="updatedDate"></slot>
          </div>
        </div>

        <div class="slot-item actions justify-end justify-md-start">
          <slot name="actions-right"></slot>
        </div>
      </div>
    </div>

    <div class="header-floating">
      <div class="header-slot right">
        <div class="slot-item toggles">
          <slot name="toggles"></slot>
        </div>
      </div>
    </div>

    <div class="header-search">
      <slot name="search"></slot>
    </div>
  </v-app-bar>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'MirrorActivityDetailAppBar',
  props: {
    app: {
      type: Boolean,
      default: false,
    },
    clippedLeft: {
      type: Boolean,
      default: false,
    },
    sidePanel: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
      selectedActivity: state => state.mirror.selectedActivity,
    }),
    username() {
      return this.currentUser.name.split(' ')[0];
    },
    usernameInitials() {
      return this.currentUser.name
        .split(' ')
        .map(name => name[0])
        .join('');
    },
    appBarHeight() {
      if (this.height) return this.height;
      return this.$vuetify.breakpoint.lgAndUp ? '88px' : '144px';
    },
  },
  methods: {
    handleBackClick() {
      if (this.$route.path.includes('/mirror/detail/')) {
        this.$router.push(
          `/mirror/${this.selectedActivity.place}/${this.selectedActivity.program_tag}`,
        );
        return;
      }
      if (this.$route.path.includes('/storage/mirror/')) {
        this.$router.replace({
          path: '/',
          query: {
            view: 'MIRROR_HISTORIC',
            content: 'mirror',
          },
        });
        return;
      }
      if (this.$route.path.includes('/mirror/')) {
        this.$router.replace({
          path: '/',
          query: {
            view: 'MIRROR',
            content: 'story',
          },
        });
        return;
      }

      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/fixes.scss';
@import '@/assets/scss/buttons.scss';

::v-deep .no-box-shadow {
  box-shadow: none !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.scale {
  transition: width 1s ease-in-out;
}

.header {
  z-index: 1;
  display: flex;
  flex-flow: wrap row;
  align-items: center;
  margin: 0;
  background-color: $color-background !important;

  &.v-app-bar--is-scrolled {
    box-shadow: 0 16px 24px rgba($color-black, 0.16);
  }

  // Content Adjustments
  ::v-deep .v-toolbar__content {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    flex: 1;
    padding: 10px 0 0 0 !important;
    margin: 0 !important;
  }

  // Divisor
  ::v-deep .v-divider {
    max-height: 24px !important;
    margin: 0 16px !important;
    align-self: center;
  }

  .header-box {
    position: relative;
    display: flex;
    flex-flow: wrap row;
    flex: 1;
    height: 56px !important;
    padding: 0;
    background-color: $color-white;
    border-radius: 4px;
    box-shadow: 0 4px 24px rgba($color-black, 0.04);
    overflow: hidden;

    @include for-size(desktop-down) {
      box-shadow: none;
      background-color: transparent;
    }
  }

  ::v-deep .divisor {
    width: 1px;
    height: 24px;
    margin: 0 24px 0 16px;
    background-color: $color-black-08;
  }

  .header-box,
  .header-floating {
    .header-slot {
      display: flex;
      align-items: center;
      height: 100%;

      &.left {
        flex: 1;
        justify-content: flex-start;

        @include for-size(phone-only) {
          width: 100%;
          max-width: 40vw;
        }
      }

      &.right {
        flex: 1;
        justify-content: flex-end;
      }

      .slot-item {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: center;
        height: 100%;

        @include for-size(phone-only) {
          width: 100%;
        }

        &.brand {
          .brand-item {
            z-index: 2;
            width: 32px;
            height: 32px;
            margin: 0 0 0 8px;
            background-image: url('../../assets/images/icon-pautaria.svg');
            background-repeat: no-repeat;
            background-size: contain;
            will-change: background-image;
            transition: all 0.8s ease-in-out;
            cursor: pointer;

            @include for-size(desktop-down) {
              width: 140px;
              height: 36px;
              margin: 0 0 0 8px;
              background-image: url('../../assets/images/marca-pautaria.svg');
              margin: 0;
            }
          }

          &::after {
            content: '';
            width: 1px;
            height: 24px;
            margin: 0 0 0 16px;
            background-color: $color-black-08;

            @include for-size(desktop-down) {
              display: none;
            }
          }
        }

        &.title,
        & .title {
          font-family: 'globotipo' !important;
          font-weight: 600 !important;
          font-style: italic;
          font-size: 1.2em !important;
          line-height: 1.2em;
          padding: 0 0 0 8px;
        }

        & .title {
          @include for-size(phone-only) {
            width: 100% !important;
          }
          padding-left: 0 !important;
        }

        &.updated-date,
        & .updated-date {
          font-style: italic;
          color: #707070;
          font-size: 10px !important;
        }

        &.search {
          position: relative;
          display: flex;
          flex-flow: wrap row;
          align-items: center;
          flex: 1;
          border-radius: 0;
          padding: 0;

          @include for-size(desktop-down) {
            display: none;
          }

          ::v-deep .v-input {
            margin-left: -8px !important;
            opacity: 1;
            will-change: opacity;
            transition: opacity 0.6s ease-in-out;

            &.v-input--is-disabled {
              opacity: 0.4;
              will-change: opacity;
              transition: opacity 0.6s ease-in-out;
              pointer-events: none;
            }

            .v-input__control {
              .v-input__slot {
                padding-right: 4px;

                label {
                  font-size: 0.88em !important;
                  font-style: italic;
                  line-height: 1.6em;
                  left: 0 !important;
                }

                .v-input__append-inner {
                  .v-icon {
                    font-size: 1.4em;
                    color: $color-black-80;
                  }
                }
              }
            }
          }
        }

        &.toggles {
          margin: 0;

          &::before {
            content: '';
            width: 1px;
            height: 24px;
            margin: 0 24px;
            background-color: $color-black-08;
          }

          ::v-deep .mode-toggle {
            box-shadow: none im !important;
            background-color: $color-white;
            border-radius: 24px;
            background-color: transparent;
            margin: 0 8px 0 0;

            &:hover {
              background-color: rgba($color-white, 0.4) !important;

              .v-btn {
                .v-icon {
                  color: $color-black-64;
                }
              }
            }

            .v-btn {
              min-height: 40px !important;
              height: 40px !important;
              min-width: 40px !important;
              width: 40px !important;
              border-radius: 100%;
              padding: 0 !important;
              background-color: transparent !important;

              .v-icon {
                font-size: 1.6em !important;
                margin: 0 !important;
              }

              &::before {
                display: none;
              }

              &.mode-toggle--active {
                background-color: $color-white !important;
                border-radius: 100%;
                box-shadow: 0 4px 24px rgba($color-black, 0.04);

                .v-icon {
                  color: $color-primary !important;
                }
              }
            }
          }
        }

        &.actions {
          margin: 0 !important;

          ::v-deep .v-btn {
            margin: 0;

            &.secondary {
              margin: 0 8px 0 0;
            }
          }
        }
      }
    }
  }

  // Display header details
  &.details {
    @include for-size(desktop-down) {
      height: 80px !important;
      padding: 0 8px !important;
      background-color: $color-white !important;

      ::v-deep .v-toolbar__content {
        height: 80px !important;
      }
    }

    .header-floating,
    .header-search {
      display: none;
    }

    .header-box {
      margin: 0;

      .header-slot {
        .back {
          margin: 0 0 0 8px;
        }

        .search,
        .brand {
          display: none;
        }

        .actions {
          margin: 0 8px 0 0;
          ::v-deep .disabled {
            pointer-events: none;
            opacity: 0.24;
          }
        }

        &.left {
          flex: 1;
        }

        &.right {
          flex: 2;
          @media screen and (max-width: 1263px) {
            flex: 1;
          }
        }
      }
    }
  }

  // Display header home
  &.main {
    .header-box {
      .header-slot {
        .menu {
          margin: 0 0 0 8px;
        }
        .back {
          display: none;
        }
        .title {
          font-family: 'globotipo' !important;
          font-weight: 400 !important;
          font-style: normal;
          font-size: 1.5em !important;
        }

        &.right {
          display: none;
        }
      }
    }

    // Display header home + sidepanel
    &.side-panel {
      .header-box {
        padding: 0 !important;

        .header-slot {
          .brand {
            display: none;
          }
        }
      }
    }

    @include for-size(desktop-down) {
      .header-floating {
        display: none;
      }

      .header-box {
        padding: 0 0 0 8px !important;
      }
    }

    // Display only mobile : search + avatar
    .header-box {
      .header-slot {
        @include for-size(desktop-down) {
          justify-content: flex-end !important;

          .brand {
            position: absolute;
            top: 0;
            left: 0;
          }

          .menu {
            margin: 0 !important;
          }
        }

        .avatar {
          display: none;

          @include for-size(desktop-down) {
            display: flex;
            margin: 0 2px 0 0;

            // Avatar
            ::v-deep .avatar {
              position: relative;
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              justify-content: center;
              width: 40px;
              height: 40px;
              margin: 0 0 0 8px;
              background-color: $color-white;
              background-image: url('../../assets/images/avatar-panda-02.svg');
              background-size: 34px 34px;
              background-position: center;
              border: solid 2px $color-white;
              will-change: all;
              transition: all 0.4s ease-in-out;
              border-radius: 100%;
              box-shadow: 0 4px 8px rgba($color-black-80, 0.08);

              &::before,
              &::after {
                content: '';
                width: 44px;
                height: 44px;
                position: absolute;
                top: -4px;
                left: -4px;
                border-radius: 100%;
                pointer-events: none;
              }

              &::before {
                content: '';
                position: absolute;
                background: linear-gradient(60deg, #f79533, #f37055, #ef4e7b);
                z-index: -1;
                animation: animatedgradient 6s ease alternate infinite;
                background-size: 400% 400%;

                @keyframes animatedgradient {
                  0% {
                    background-position: 0% 50%;
                  }
                  50% {
                    background-position: 100% 50%;
                  }
                  100% {
                    background-position: 0% 50%;
                  }
                }
              }

              &::after {
                z-index: 2;
                width: 36px;
                height: 36px;
                top: 2px;
                left: 2px;
                background: linear-gradient(
                  0deg,
                  $color-primary 0%,
                  $color-primary-light 100%
                );
                opacity: 0;
              }

              &:hover {
                background-image: url('../../assets/images/avatar-panda-02.svg');
              }
            }
          }
        }
      }
    }

    .header-search {
      display: flex;
      width: 100%;
      margin: 0;

      ::v-deep .v-input {
        opacity: 1;
        will-change: opacity;
        transition: opacity 0.6s ease-in-out;

        &.v-input--is-disabled {
          opacity: 0.4;
          will-change: opacity;
          transition: opacity 0.6s ease-in-out;
          pointer-events: none;
        }

        .v-input__control {
          min-height: 48px !important;

          .v-input__slot {
            padding: 0 16px;
            min-height: 48px !important;

            label {
              font-size: 0.88em !important;
              font-style: italic;
              line-height: 1.6em;
              left: 0 !important;
            }

            .v-input__append-inner {
              .v-icon {
                font-size: 1.4em;
                color: $color-black-80;
              }
            }
          }
        }
      }
    }
  }
}
</style>
